/**
 * SessionManager - Core class for session health monitoring
 *
 * Handles: periodic health checks, activity tracking, visibility-based refresh,
 * cross-tab logout/token sync, and inactivity timeout.
 */

import {
  getAuthCookie,
  getRefreshCookie,
  setAuthCookie,
  setRefreshCookie,
  clearAuthCookie,
  clearRefreshCookie,
  clearOrgCookie,
  getSessionCookie,
  setSessionCookie,
  isTokenExpired,
  needsTokenRefresh,
  isCookieAuthValid,
  clearAllAuthCookies,
  redirectToMasterLogin,
  getInactivityDuration,
  updateSessionActivity,
  type LogoutReason,
} from '../cookieAuth'
import {
  SESSION_CHECK_INTERVAL,
  ACTIVITY_DEBOUNCE_MS,
  INACTIVITY_TIMEOUT_MS,
  VISIBILITY_REFRESH_THRESHOLD,
  isValidJWTStructure,
  type RefreshOutcome,
} from './constants'
import { refreshTokenWithRetry, type TokenRefreshCallbacks } from './tokenRefresh'
import { classifyMasterQuotechRefresh } from './silentMasterRefresh'
import {
  syncTokenToLocalStorage,
  clearLocalStorageAuth,
  broadcastLogout,
  broadcastTokenSync,
  createLogoutListener,
  createTokenSyncListener,
  decodeJwtUserId,
  isStaleCookieJar,
} from './tokenSync'

export class SessionManager {
  private checkInterval: ReturnType<typeof setInterval> | null = null
  private activityTimeout: ReturnType<typeof setTimeout> | null = null
  private isMonitoring = false
  private lastActivityDebounced = 0
  private lastVisibilityChange = Date.now()
  private isRefreshingRef = { current: false }

  private onSessionExpired?: (reason: LogoutReason) => void
  private onTokenRefreshed?: (newToken: string) => void

  // Event listeners (bound for cleanup)
  private logoutListener: ((e: StorageEvent) => void) | null = null
  private tokenSyncListener: ((e: StorageEvent) => void) | null = null

  constructor(options?: {
    onSessionExpired?: (reason: LogoutReason) => void
    onTokenRefreshed?: (newToken: string) => void
  }) {
    this.onSessionExpired = options?.onSessionExpired
    this.onTokenRefreshed = options?.onTokenRefreshed
  }

  // ---- PUBLIC API ----

  startMonitoring(): void {
    if (this.isMonitoring) {
      console.warn('[@quotech/auth] SessionManager already monitoring')
      return
    }
    this.isMonitoring = true
    console.log('[@quotech/auth] SessionManager started')

    // Reset session cookie if stale
    const token = getAuthCookie()
    if (token && isValidJWTStructure(token)) {
      const session = getSessionCookie()
      const inactivity = getInactivityDuration()
      if (!session || inactivity >= INACTIVITY_TIMEOUT_MS) {
        console.log('[@quotech/auth] Resetting session activity on startup')
        setSessionCookie(typeof window !== 'undefined' ? window.location.hostname : 'dashboard')
      }
    }

    this.safeCheckSessionHealth()
    this.checkInterval = setInterval(() => this.safeCheckSessionHealth(), SESSION_CHECK_INTERVAL)
    this.setupActivityListeners()
    this.setupLogoutListener()
    this.setupVisibilityListener()
    this.setupTokenSyncListener()
  }

  stopMonitoring(): void {
    if (!this.isMonitoring) return
    this.isMonitoring = false
    console.log('[@quotech/auth] SessionManager stopped')

    if (this.checkInterval) { clearInterval(this.checkInterval); this.checkInterval = null }
    if (this.activityTimeout) { clearTimeout(this.activityTimeout); this.activityTimeout = null }
    this.removeActivityListeners()
    this.removeLogoutListener()
    this.removeVisibilityListener()
    this.removeTokenSyncListener()
  }

  recordActivity(): void {
    const now = Date.now()
    if (now - this.lastActivityDebounced < ACTIVITY_DEBOUNCE_MS) return
    this.lastActivityDebounced = now
    updateSessionActivity()
  }

  logout(reason: LogoutReason = 'logged_out'): void {
    clearAllAuthCookies()
    clearLocalStorageAuth()
    broadcastLogout()
    this.onSessionExpired?.(reason)
    redirectToMasterLogin(reason)
  }

  isSessionValid(): boolean {
    const token = getAuthCookie()
    if (!token || !isValidJWTStructure(token)) return false
    return isCookieAuthValid()
  }

  async forceRefresh(): Promise<boolean> {
    return (await this.doRefresh()) === 'refreshed'
  }

  // ---- HEALTH CHECKS ----

  private safeCheckSessionHealth(): void {
    try { this.checkSessionHealth() } catch (e) { console.error('[@quotech/auth] Session health check error:', e) }
  }

  private async checkSessionHealth(): Promise<void> {
    // BUG-2026-06-26-STALE-COOKIE-JAR-LOGOUT (identity-drift family):
    // Before reading the cookie jar to decide session health, reconcile it
    // against the live in-store bearer. A prior account's cookies (expired
    // access + blacklisted refresh) survive a login as a NEW user on the
    // shared `.quotech.ai` jar, and reading them here makes both refresh
    // legs answer 401 → the freshly-authenticated user is signed out even
    // though their own bearer is valid and their API calls succeed. The
    // reconcile clears the stale cross-identity cookies and re-seeds the jar
    // from the current user so the checks below operate on the RIGHT user.
    this.reconcileStaleCookieJar()

    // BUG-2026-06-26-SESSION-MONITOR-IGNORES-LIVE-BEARER (STALE-COOKIE-JAR
    // sibling): anchor the session-life decision on the LIVE BEARER the API
    // clients actually authenticate with, not solely on the cookie jar.
    //
    // Two ways the cookie jar lies about a perfectly-alive session, both
    // proven in prod for a v1 user (abhishek@eyedream.info, user 51):
    //   (a) the `quotech_auth` access cookie is expired/stale while the
    //       localStorage bearer (IdToken/accessTokennew) the reads use is
    //       fresh — the cookie just drifted behind the bearer; and
    //   (b) a v1-login user has NO Auth-v2 session, so the silent master
    //       refresh ALWAYS answers 401 ('dead'), and a missing/foreign v1
    //       refresh credential answers 'no_material' — which together trip
    //       the `master==='dead' && v1==='no_material'` sign-out gate below
    //       and log the user out even though their bearer is valid and every
    //       authenticated read (and the v1 refresh, when the jar isn't stale)
    //       succeeds.
    //
    // If a structurally-valid, UNEXPIRED, same-or-anchoring live bearer
    // exists, the session is alive: mirror it into a stale/expired/missing
    // access cookie so the checks below take the healthy path, and skip the
    // doomed refresh storm + sign-out entirely. A genuinely-expired bearer
    // (e.g. the Auth-v2 zombie of BUG-2026-06-11) is NOT preserved here, so
    // that real sign-out still fires; cookie-only Auth-v2 users have no
    // localStorage bearer to anchor on and fall through unchanged.
    const liveBearer = this.readLiveBearer()
    if (liveBearer && isValidJWTStructure(liveBearer) && !isTokenExpired(liveBearer)) {
      const cookie = getAuthCookie()
      const cookieStale = !cookie || !isValidJWTStructure(cookie) || isTokenExpired(cookie)
      if (cookieStale) {
        // The cookie jar drifted behind the (valid) live bearer. Mirror the
        // bearer in and treat the session as healthy THIS tick — do NOT storm
        // the refresh endpoints or sign out. Next tick reads a fresh cookie.
        console.log('[@quotech/auth] Live bearer valid but cookie stale — mirroring bearer into the jar (no refresh / no sign-out)')
        setAuthCookie(liveBearer)
        syncTokenToLocalStorage(liveBearer)
        return
      }
      // Cookie is fresh and (after reconcile) belongs to the live identity —
      // fall through to the normal path so PROACTIVE pre-expiry refresh, which
      // a v2 session relies on, still runs. The expired-cookie death path is
      // unreachable from here, so a valid bearer is never signed out.
    }

    let token = getAuthCookie()

    // BUG-2026-06-04-LOGOUT-EVERY-HOUR + BUG-2026-05-25-STAY-LOGGED-IN:
    // The session must live until the user explicitly logs out OR the
    // 30-day refresh token is definitively expired/revoked. It must NOT
    // die on access-token expiry (we refresh), on inactivity, or on a
    // transient refresh failure (network blip / 5xx / 429 / a refresh
    // cookie that's HttpOnly under Auth v2 and therefore invisible to JS).
    //
    // INACTIVITY: the previous 30-min idle sign-out (remember-me off) was
    // removed deliberately — per product requirement the 30-day refresh
    // token is the session lifetime. We still record activity for the
    // session cookie's `last_activity` telemetry; we just never use it to
    // tear the session down.

    // Step 1 — cookie missing / malformed: try the master silent refresh
    // (the authority for the 30-day HttpOnly v2 `quotech_refresh` cookie).
    // Only sign out on a DEFINITIVE 'dead' verdict; preserve on transient.
    if (!token || !isValidJWTStructure(token)) {
      const master = await this.attemptSilentMasterRefresh('cookie_missing_or_invalid')
      if (master === 'refreshed') {
        token = getAuthCookie()
        if (!token || !isValidJWTStructure(token)) return // recovered flag but no usable token yet — preserve, retry next tick
      } else if (master === 'dead') {
        this.handleSessionDeath('invalid'); return
      } else {
        return // transient — preserve session, retry on the next tick
      }
    }

    if (isTokenExpired(token)) {
      // Two-step recovery: master silent refresh (v2 HttpOnly cookie)
      // first, then the legacy v1 SimpleJWT refresh. Sign out ONLY when
      // the master path is definitively 'dead' AND the v1 path is either
      // 'dead' too or has no refresh material at all ('no_material').
      // Any 'transient' from either path preserves the session for the
      // next tick.
      //
      // BUG-2026-06-11-ZOMBIE-SESSION: requiring a literal v1 'dead' here
      // made sign-out unreachable for Auth-v2-only users — their v1 leg
      // has no JS-readable refresh cookie (HttpOnly) so it perpetually
      // answered 'transient', and when the 30-day v2 refresh cookie
      // expired the app sat on a half-rendered page spamming 401s on
      // every endpoint instead of redirecting to login. 'no_material'
      // (v1 has nothing to try, ever) must not veto the master's
      // definitive 401/403 verdict.
      const master = await this.attemptSilentMasterRefresh('access_jwt_expired')
      if (master === 'refreshed') {
        const fresh = getAuthCookie()
        if (fresh && isValidJWTStructure(fresh)) {
          syncTokenToLocalStorage(fresh)
          this.onTokenRefreshed?.(fresh)
        }
        return
      }
      const v1 = await this.doRefresh()
      if (v1 === 'refreshed') {
        const fresh = getAuthCookie()
        if (fresh && isValidJWTStructure(fresh)) {
          syncTokenToLocalStorage(fresh)
          this.onTokenRefreshed?.(fresh)
        }
        return
      }
      if (master === 'dead' && (v1 === 'dead' || v1 === 'no_material')) {
        this.handleSessionDeath('session_expired')
        return
      }
      return // at least one path is transient — preserve, retry next tick
    } else if (needsTokenRefresh()) {
      // Proactive pre-expiry refresh. Under Auth v2 the v1 `doRefresh`
      // path is a no-op (its refresh cookie is HttpOnly), so try the
      // master silent refresh first — that's what actually renews a v2
      // session before it expires. Never sign out proactively.
      const master = await this.attemptSilentMasterRefresh('access_jwt_near_expiry')
      if (master === 'refreshed') {
        const fresh = getAuthCookie()
        if (fresh && isValidJWTStructure(fresh)) {
          syncTokenToLocalStorage(fresh)
          this.onTokenRefreshed?.(fresh)
        }
        return
      }
      await this.doRefresh()
    }

    syncTokenToLocalStorage(token)
  }

  /** Wrap the silent master refresh in a per-instance in-flight guard
   * so concurrent health checks (e.g. visibility-regain firing while
   * the 60s tick is mid-flight) do not stack POSTs to
   * /api/auth/v2/refresh/. Logs each verdict for audit-trail parity
   * with the original `handleSessionDeath` log lines so operators
   * grepping their browser console can see "refresh tried + outcome"
   * for every save the session was offered.
   *
   * Returns true iff the master refresh succeeded AND a fresh
   * `quotech_auth` cookie now lives in `document.cookie`. The
   * caller must re-read the cookie itself — we deliberately do
   * not cache the verdict across calls. */
  private silentRefreshInFlight: Promise<RefreshOutcome> | null = null
  private async attemptSilentMasterRefresh(reason: string): Promise<RefreshOutcome> {
    if (this.silentRefreshInFlight) return this.silentRefreshInFlight
    const inflight = (async (): Promise<RefreshOutcome> => {
      try {
        console.log(`[@quotech/auth] SessionManager attempting silent master refresh (reason=${reason})`)
        const outcome = await classifyMasterQuotechRefresh()
        console.log(`[@quotech/auth] SessionManager silent master refresh outcome (reason=${reason}): ${outcome}`)
        return outcome
      } catch (err) {
        // Never let an unexpected throw be read as a dead session.
        console.warn('[@quotech/auth] SessionManager silent master refresh threw:', err)
        return 'transient'
      }
    })()
    this.silentRefreshInFlight = inflight
    try {
      return await inflight
    } finally {
      this.silentRefreshInFlight = null
    }
  }

  /** The live access bearer the API clients actually send. After login it
   *  is the authoritative "who am I" — written by every login path into
   *  localStorage (`IdToken` / `accessTokennew`) and kept fresh by
   *  `syncTokenToLocalStorage` on every successful refresh. Used as the
   *  identity anchor to detect a stale cross-account cookie jar. */
  private readLiveBearer(): string | null {
    let stored: string | null = null
    if (typeof localStorage !== 'undefined') {
      try {
        stored = localStorage.getItem('accessTokennew')
          || localStorage.getItem('IdToken')
          || localStorage.getItem('accessToken')
      } catch {
        stored = null
      }
    }
    if (stored) return stored
    // BUG-2026-06-26-COOKIE-BEARER-NOT-ANCHORED (STALE-COOKIE-JAR family): some
    // login / cross-subdomain SSO paths seed ONLY the domain-shared `.quotech.ai`
    // `quotech_auth` access cookie and never write the bearer into THIS origin's
    // localStorage (localStorage is per-origin; the cookie is shared). The API
    // clients still authenticate via that cookie (reads return 200), but with
    // localStorage empty the WHOLE identity-anchor family — reconcileStaleCookieJar,
    // the live-bearer death-gate anchor above, and performSingleRefresh's
    // cross-identity guard — FAILED OPEN (no visible identity), so a PRIOR
    // account's leftover `quotech_refresh` cookie was never cleared and got POSTed
    // every monitor tick (prod: valid user 51 vs blacklisted user 15 →
    // refresh-token 401 storm) while the doomed v2/refresh fired too. Anchor on
    // the access cookie the API actually uses. Structurally-valid is enough here
    // even if expired: the death-gate anchor re-checks expiry itself, and the
    // reconcile only needs a decodable user_id to recognise + clear a foreign jar.
    const cookieBearer = getAuthCookie()
    return cookieBearer && isValidJWTStructure(cookieBearer) ? cookieBearer : null
  }

  /** Detect and heal a cookie jar left behind by a DIFFERENT account.
   *
   *  BUG-2026-06-26-STALE-COOKIE-JAR-LOGOUT: the `.quotech.ai` cookies
   *  (`quotech_auth` access + `quotech_refresh`) are domain-shared and are
   *  NOT cleared by every login path, so after switching accounts they can
   *  still hold the PRIOR user's expired access + revoked/blacklisted
   *  refresh tokens. `checkSessionHealth` would then refresh the wrong
   *  (dead) credential, get 401 on both legs, and sign out the current,
   *  perfectly-valid user.
   *
   *  This compares the cookie tokens' owning `user_id` to the live bearer's.
   *  On a mismatch it clears ONLY the identity-scoped cookies (access /
   *  refresh / org — never remember-me or the session telemetry cookie) and
   *  re-seeds them from the current user's localStorage credentials so the
   *  jar reflects the logged-in identity. No-op when there is no live bearer
   *  to anchor on (e.g. a cookie-only Auth-v2 user), when the ids match, or
   *  when either id is indeterminate — so it can never sign anyone out and
   *  never fires on a legitimate same-user session.
   */
  private reconcileStaleCookieJar(): void {
    const bearer = this.readLiveBearer()
    if (!bearer || !isValidJWTStructure(bearer)) return
    const bearerId = decodeJwtUserId(bearer)
    if (!bearerId) return

    const accessId = decodeJwtUserId(getAuthCookie())
    const refreshId = decodeJwtUserId(getRefreshCookie())
    if (!isStaleCookieJar({ bearerId, accessId, refreshId })) return

    console.warn(
      '[@quotech/auth] Stale cross-identity cookie jar detected '
      + `(bearer=${bearerId}, cookie_access=${accessId}, cookie_refresh=${refreshId}). `
      + 'Clearing the prior-account cookies and re-seeding from the current '
      + 'user so they cannot force a logout (BUG-2026-06-26-STALE-COOKIE-JAR-LOGOUT).',
    )
    // Identity-scoped cookies only — preserve remember-me + session telemetry.
    clearAuthCookie()
    clearRefreshCookie()
    clearOrgCookie()

    // Re-seed the jar from the live identity so the very next read below
    // sees THIS user's token instead of an empty jar (which would route to
    // the master-refresh 'dead' sign-out). Only seed credentials that
    // structurally belong to the current user and are not already expired.
    if (!isTokenExpired(bearer)) setAuthCookie(bearer)
    try {
      const lsRefresh = typeof localStorage !== 'undefined' ? localStorage.getItem('refreshToken') : null
      if (lsRefresh && isValidJWTStructure(lsRefresh) && decodeJwtUserId(lsRefresh) === bearerId) {
        setRefreshCookie(lsRefresh)
      }
    } catch { /* localStorage may throw in private mode — best effort */ }
  }

  private async doRefresh(): Promise<RefreshOutcome> {
    const callbacks: TokenRefreshCallbacks = {
      onTokenRefreshed: this.onTokenRefreshed,
      syncTokenToLocalStorage,
      broadcastTokenSync,
    }
    return refreshTokenWithRetry(callbacks, this.isRefreshingRef)
  }

  private handleSessionDeath(reason: LogoutReason): void {
    if (!this.isMonitoring) return
    this.stopMonitoring()
    clearAllAuthCookies()
    clearLocalStorageAuth()
    this.onSessionExpired ? this.onSessionExpired(reason) : redirectToMasterLogin(reason)
  }

  // ---- ACTIVITY LISTENERS ----

  private activityHandler = (): void => { this.recordActivity() }

  private setupActivityListeners(): void {
    if (typeof window === 'undefined') return
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach((e) => window.addEventListener(e, this.activityHandler, { passive: true }))
  }
  private removeActivityListeners(): void {
    if (typeof window === 'undefined') return
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach((e) => window.removeEventListener(e, this.activityHandler))
  }

  // ---- VISIBILITY ----

  private visibilityHandler = (): void => {
    if (document.visibilityState === 'visible') {
      const hidden = Date.now() - this.lastVisibilityChange
      if (hidden > VISIBILITY_REFRESH_THRESHOLD) {
        console.log(`[@quotech/auth] Tab visible after ${Math.round(hidden / 1000)}s, checking session`)
        this.safeCheckSessionHealth()
      }
    } else {
      this.lastVisibilityChange = Date.now()
    }
  }
  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') return
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }
  private removeVisibilityListener(): void {
    if (typeof document === 'undefined') return
    document.removeEventListener('visibilitychange', this.visibilityHandler)
  }

  // ---- CROSS-TAB ----

  private setupLogoutListener(): void {
    if (typeof window === 'undefined') return
    this.logoutListener = createLogoutListener(() => this.handleSessionDeath('logged_out'))
    window.addEventListener('storage', this.logoutListener)
  }
  private removeLogoutListener(): void {
    if (typeof window === 'undefined' || !this.logoutListener) return
    window.removeEventListener('storage', this.logoutListener)
    this.logoutListener = null
  }

  private setupTokenSyncListener(): void {
    if (typeof window === 'undefined') return
    this.tokenSyncListener = createTokenSyncListener((token) => syncTokenToLocalStorage(token))
    window.addEventListener('storage', this.tokenSyncListener)
  }
  private removeTokenSyncListener(): void {
    if (typeof window === 'undefined' || !this.tokenSyncListener) return
    window.removeEventListener('storage', this.tokenSyncListener)
    this.tokenSyncListener = null
  }
}
